import { useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash';

import { formatAsset, getRequestOptions, sortAsset } from '../utils';

import { jsonApi } from '~/clients';
import { jsonaDataFormatter } from '~/utils/jsonaDataFormatter';
import { ASSET_TYPES } from '~/constants';

const getParentAssetKey = assetType => {
  if (assetType === ASSET_TYPES.intervention_unit.type) {
    return 'intervention_unit_id';
  }
  return assetType;
};

export function useSecondaryAssetSelectData({
  appKey,
  assetType,
  assetId,
  company,
  parentAssetType,
  parentAssetId,
  queryParams,
  assetFields,
  activeWellId,
  isRequiredAssetId,
}) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    async function loadAssets() {
      const response = await jsonApi.mapAssetGetRequest(assetType)({
        ...getRequestOptions(assetType, assetFields),
        per_page: 10000,
        company: company !== 'all' ? company : null,
        [getParentAssetKey(parentAssetType)]: parentAssetId,
        ...(appKey && { app_key: appKey }),
        ...queryParams,
      });

      const deserializedResponse = jsonaDataFormatter.deserialize(response);

      setAssets(
        deserializedResponse
          .map(asset => formatAsset({ asset, activeWellId, isRequiredAssetId }))
          .sort(sortAsset)
      );
      setLoading(false);
    }
    loadAssets();
  }, [assetType, assetId, parentAssetType, parentAssetId, appKey, activeWellId, company]);

  return { assets, loading };
}

const getAssetFields = (assetType, assetFields) => {
  if (assetType === ASSET_TYPES.pad.type) {
    return [...assetFields, 'pad.active', 'pad.parent_frac_fleet_id', 'pad.current'];
  } else if (assetType === ASSET_TYPES.intervention_unit.type) {
    return [...assetFields, 'intervention_unit.active', 'intervention_unit.current'];
  }

  return assetFields;
};

export function usePrimaryAssetSelectData({
  appKey,
  assetType,
  company,
  groups,
  isRequiredAssetId,
  parentAssetId,
  parentAssetType,
  searchText,
  assetFields,
  currentValue,
  setCurrentOption,
  onActiveAssetIdChange,
}) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const prevCompany = useRef(null);

  const loadAssets = async group => {
    try {
      const currentAssetType = group?.type || assetType;
      const requestParams = { per_page: 10000 };
      if (parentAssetType && parentAssetId) requestParams[parentAssetType] = parentAssetId;
      if (company && company !== 'all') requestParams.company = company;
      if (searchText) requestParams.search = searchText;
      if (appKey) requestParams.app_key = appKey;

      prevCompany.current = company;

      const requestPromise = jsonApi.mapAssetGetRequest(currentAssetType)({
        ...getRequestOptions(currentAssetType, getAssetFields(currentAssetType, assetFields)),
        ...requestParams,
      });

      const response = await requestPromise;
      const deserializedResponse = jsonaDataFormatter.deserialize(response);

      const responseAssets = deserializedResponse
        .map(asset =>
          formatAsset({ asset, group: group?.name, isRequiredAssetId, isPrimaryAsset: true })
        )
        .filter(Boolean)
        .sort(sortAsset);

      if (prevCompany.current === company) {
        setAssets(prevState => [...prevState, ...responseAssets]);
      }
      if (currentValue && currentAssetType === assetType) {
        const currentAsset = responseAssets.find(
          asset => currentValue === asset.id || (asset.assetId && currentValue === asset.assetId)
        );
        if (!isEmpty(currentAsset)) {
          setCurrentOption(currentAsset);
        }

        if (currentAssetType === ASSET_TYPES.rig.type) {
          onActiveAssetIdChange(currentAsset?.activeWellId);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (company) {
      setAssets([]);
    }
  }, [company]);

  useEffect(() => {
    const loadEverything = async () => {
      try {
        setLoading(true);

        if (groups) {
          await Promise.all(groups.map(group => loadAssets(group)));
        } else {
          await loadAssets();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadEverything();
  }, [assetType, parentAssetType, parentAssetId, company, appKey, groups?.length]);

  return { assets: loading ? [] : assets, loading };
}
